/**
 * BatchRunDetails - Detailed view of a batch run with results
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, CheckCircle2, XCircle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { getBatchStatus, getBatchImages, cancelBatch, pollBatch } from '@/lib/api/batch';
import { Button, Badge, EmptyState } from '@/components/v2/ui';
import { BatchImageGallery } from './BatchImageGallery';
import { BatchInfoCard } from './BatchInfoCard';
import { BatchProcessingState } from './BatchProcessingState';
import { useBatchAutoPolling } from './hooks/useBatchAutoPolling';
import { cn } from '@/lib/utils';

interface BatchRunDetailsProps {
  runId: string;
}

const statusConfig = {
  pending: { icon: Clock, label: 'Ausstehend', variant: 'default' as const, color: 'text-neutral-500' },
  submitted: { icon: Clock, label: 'Gesendet', variant: 'info' as const, color: 'text-primary-500' },
  running: { icon: Loader2, label: 'In Bearbeitung', variant: 'warning' as const, color: 'text-amber-500', spin: true },
  completed: { icon: CheckCircle2, label: 'Fertig', variant: 'success' as const, color: 'text-emerald-500' },
  failed: { icon: XCircle, label: 'Fehlgeschlagen', variant: 'error' as const, color: 'text-red-500' },
  cancelled: { icon: XCircle, label: 'Abgebrochen', variant: 'default' as const, color: 'text-neutral-500' },
};

export function BatchRunDetails({ runId }: BatchRunDetailsProps) {
  const { t } = useTranslation();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [lastPollResult, setLastPollResult] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['batch', 'status', runId],
    queryFn: () => getBatchStatus(runId),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'completed' || s === 'failed' || s === 'expired' ? false : 5000;
    },
  });

  // Auto-polling: Actively trigger poll action for submitted/running batches
  const { isPolling: autoPolling } = useBatchAutoPolling({
    runId,
    status: data?.status,
    enabled: true,
    interval: 15000, // Poll Google API every 15 seconds
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelBatch(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch', 'status', runId] });
      queryClient.invalidateQueries({ queryKey: ['batch', 'runs'] });
      setShowCancelConfirm(false);
    },
  });

  const pollMutation = useMutation({
    mutationFn: () => pollBatch(runId),
    onSuccess: (result) => {
      const statusText = result.gemini_status || result.status;
      const msg = `Status: ${statusText} (${result.completed_items} fertig, ${result.failed_items} fehlgeschlagen)`;
      setLastPollResult(msg);
      toast.info(msg);
      queryClient.invalidateQueries({ queryKey: ['batch', 'status', runId] });
      queryClient.invalidateQueries({ queryKey: ['batch', 'images', runId] });
      queryClient.invalidateQueries({ queryKey: ['batch', 'runs'] });
    },
    onError: (err) => {
      toast.error(`Poll fehlgeschlagen: ${err}`);
    },
  });

  const { data: imagesData, isLoading: imagesLoading } = useQuery({
    queryKey: ['batch', 'images', runId],
    queryFn: () => getBatchImages(runId),
    enabled: data?.status === 'completed',
  });

  const canCancel = data && ['pending', 'submitted', 'running'].includes(data.status);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!data || !data.success) {
    return (
      <EmptyState
        icon={<XCircle className="h-12 w-12 text-red-500" />}
        title={t('errors.loading')}
        description={data?.error || 'Batch-Details konnten nicht geladen werden'}
      />
    );
  }

  const config = statusConfig[data.status as keyof typeof statusConfig] || statusConfig.pending;
  const Icon = config.icon;
  const canPoll = ['pending', 'submitted', 'running'].includes(data.status);

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('de-DE', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">Batch Details</h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">ID: {runId.slice(0, 16)}...</p>
        </div>
        <Badge variant={config.variant} icon={<Icon className={cn('h-4 w-4', config.color, 'spin' in config && config.spin && 'animate-spin')} />}>
          {config.label}
        </Badge>
      </div>

      {/* Last Update + Poll Button */}
      <div className="flex items-center justify-between p-3 rounded-lg bg-neutral-100 dark:bg-neutral-800">
        <div className="flex items-center gap-3">
          <div className="text-sm">
            <span className="text-neutral-500 dark:text-neutral-400">Letzter Abruf: </span>
            <span className="text-neutral-700 dark:text-neutral-300 font-medium">
              {dataUpdatedAt ? formatTime(dataUpdatedAt) : '-'}
            </span>
          </div>
          {canPoll && (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary-100 dark:bg-primary-950/50 border border-primary-200 dark:border-primary-800">
              <div className="h-2 w-2 rounded-full bg-primary-500 animate-pulse" />
              <span className="text-xs font-medium text-primary-700 dark:text-primary-300">
                Auto-Polling aktiv
              </span>
            </div>
          )}
        </div>
        {canPoll && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => pollMutation.mutate()}
            disabled={pollMutation.isPending || autoPolling}
            iconLeft={pollMutation.isPending || autoPolling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          >
            Manuell aktualisieren
          </Button>
        )}
      </div>

      {/* Last Poll Result */}
      {lastPollResult && (
        <div className="p-3 rounded-lg bg-primary-50 dark:bg-primary-950/30 border border-primary-200 dark:border-primary-800 text-sm text-primary-700 dark:text-primary-300">
          {lastPollResult}
        </div>
      )}

      {/* Info Grid */}
      <div className="grid grid-cols-2 gap-4">
        <BatchInfoCard label="Erstellt" value={formatDate(data.created_at)} />
        <BatchInfoCard label="Abgeschlossen" value={formatDate(data.completed_at)} />
        {data.reserved_credits && (
          <BatchInfoCard label="Reservierte Credits" value={`${data.reserved_credits.toFixed(2)} Credits`} variant="orange" />
        )}
        {data.batch_name && <BatchInfoCard label="Gemini Batch" value={data.batch_name} mono />}
      </div>

      {/* Error Message */}
      {data.error_message && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
          <div className="flex items-start gap-3">
            <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-red-900 dark:text-red-100 mb-1">Fehler</div>
              <div className="text-sm text-red-700 dark:text-red-300">{data.error_message}</div>
            </div>
          </div>
        </div>
      )}

      {/* Results Section */}
      {data.status === 'completed' && (
        <BatchImageGallery images={imagesData?.images || []} stats={imagesData?.stats || null} isLoading={imagesLoading} />
      )}

      {/* Processing State */}
      {(data.status === 'running' || data.status === 'submitted') && (
        <BatchProcessingState
          canCancel={!!canCancel}
          showCancelConfirm={showCancelConfirm}
          setShowCancelConfirm={setShowCancelConfirm}
          cancelMutation={cancelMutation}
        />
      )}
    </div>
  );
}
